import { normalizeOpportunityValue } from '@dashboardApp/helpers/insightScores';
import { normalizeStringOrNull } from '@dashboardApp/helpers/normalize';
import { buildTrendingProductIdeasCategoryRoute, buildTrendingProductIdeasRoute } from '@dashboardApp/helpers/researchLinks';

function toNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const numericValue = Number(value);

    return Number.isNaN(numericValue) ? null : numericValue;
}

function mapOpportunityScore(value) {
    const normalizedValue = normalizeOpportunityValue(value);
    const scores = {
        strong_buy: 80,
        buy: 60,
        consider: 40,
        avoid: 20,
        strong_avoid: 0,
    };

    return scores[normalizedValue] ?? null;
}

function resolveInterestTrend(record) {
    if (record?.interest_trend && typeof record.interest_trend === 'object') {
        return record.interest_trend;
    }

    const interestTrends = record?.interest_trends;

    if (!interestTrends || typeof interestTrends !== 'object') {
        return {};
    }

    const documentWindow = typeof record?.id === 'string' && record.id.includes(':')
        ? record.id.split(':')[1]
        : null;
    const keys = [
        documentWindow ? `trend-${documentWindow}` : null,
        'trend-12mo',
        'trend-6mo',
        'trend-3mo',
        'trend-24mo',
    ].filter(Boolean);

    for (const key of keys) {
        if (interestTrends[key] && typeof interestTrends[key] === 'object') {
            return interestTrends[key];
        }
    }

    return {};
}

export function mapTrendingIdeaToViewModel(record, options = {}) {
    const interestTrend = resolveInterestTrend(record);
    const slug = record?.slug || null;
    const categorySlug = record?.category_slug || null;
    const marketOpportunity = record?.market_opportunity || null;
    const sellPrice = record?.sell_price || {};
    const status = typeof record?.status === 'string' ? record.status.trim().toLowerCase() : null;
    const isPending = record?.is_pending_row === true && status === 'pending';
    const title = record?.title || record?.term || '';
    const recordId = record?._jv?.id || record?.id || '';
    const reportId = normalizeStringOrNull(record?.report_id ?? (isPending ? recordId : null));
    const reportRoute = buildReportRoute({
        slug,
        reportId,
        interestTrendWindow: interestTrend.window || '12mo',
        isPending,
        preferTrackedReportRoute: options.preferTrackedReportRoute === true,
    });

    return {
        id: recordId,
        report_id: reportId,
        product_id: toNumber(record?.product_id ?? record?.source_id ?? (!isPending ? recordId : null)),
        name: title,
        term: title,
        slug,
        thumbnail: record?.image_url || null,
        category: record?.category || null,
        categoryRoute: buildTrendingProductIdeasCategoryRoute(categorySlug),
        url: null,
        reportRoute,
        status,
        isPending,
        isReadyForReports: record?.is_ready_for_reports ?? null,
        pendingSince: record?.pending_since || null,
        avgPrice: toNumber(record?.avg_sell_price || sellPrice.average), 
        trend: toNumber(interestTrend.change_ratio),
        humanTrend: toNumber(interestTrend.change_pct),
        trendValues: Array.isArray(interestTrend.values) ? interestTrend.values : [],
        interestTrends: record?.interest_trends && typeof record.interest_trends === 'object' ? record.interest_trends : {},
        scores: {
            opportunity: mapOpportunityScore(marketOpportunity),
            opportunity_text: normalizeOpportunityValue(marketOpportunity) || null,
            demand: toNumber(record?.demand_percentage),
            demand_text: record?.demand_level || null,
            competition: toNumber(record?.competition_percentage),
            competition_text: record?.competition_level || null,
        },
    };
}

function buildReportRoute({
    slug,
    reportId,
    interestTrendWindow,
    isPending,
    preferTrackedReportRoute,
}) {
    if (isPending) {
        return null;
    }

    if (preferTrackedReportRoute && reportId) {
        return {
            name: 'research-tracked-product-idea-report',
            params: { reportId },
            query: {
                interest_trend_window: interestTrendWindow,
            },
        };
    }

    if (slug) {
        return {
            name: 'research-trending-product-idea-report',
            params: { slug },
            query: {
                interest_trend_window: interestTrendWindow,
            },
        };
    }

    return buildTrendingProductIdeasRoute();
}
